import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Home/Components/footer/Footer";
import "./Gallery.css";

import { API_BASE_URL } from "../../config";

const GalleryDetail = () => {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const [folder, setFolder] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxIdx, setLightboxIdx] = useState(null);
    const [slideshow, setSlideshow] = useState(false);
    const [slideshowIdx, setSlideshowIdx] = useState(0);
    const [ssPlaying, setSsPlaying] = useState(true);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        Promise.all([
            fetch(`${API_BASE_URL}/api/gallery/folders/${folderId}`).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/gallery/folders/${folderId}/images`).then(r => r.json()),
        ]).then(([f, imgs]) => {
            setFolder(f);
            setImages(Array.isArray(imgs) ? imgs : []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [folderId]);

    // Slideshow auto-advance
    useEffect(() => {
        if (!slideshow || !ssPlaying || images.length === 0) return;
        const timer = setInterval(() => {
            setSlideshowIdx(i => (i + 1) % images.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [slideshow, ssPlaying, images.length]);

    // Keyboard nav
    const handleKey = useCallback((e) => {
        if (lightboxIdx !== null) {
            if (e.key === "ArrowRight") setLightboxIdx(i => Math.min(i + 1, images.length - 1));
            if (e.key === "ArrowLeft") setLightboxIdx(i => Math.max(i - 1, 0));
            if (e.key === "Escape") setLightboxIdx(null);
        }
        if (slideshow) {
            if (e.key === "ArrowRight") setSlideshowIdx(i => (i + 1) % images.length);
            if (e.key === "ArrowLeft") setSlideshowIdx(i => (i - 1 + images.length) % images.length);
            if (e.key === "Escape") setSlideshow(false);
            if (e.key === " ") setSsPlaying(p => !p);
        }
    }, [lightboxIdx, slideshow, images.length]);

    useEffect(() => {
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [handleKey]);

    const openSlideshow = (idx = 0) => {
        setSlideshowIdx(idx);
        setSsPlaying(true);
        setSlideshow(true);
    };

    if (loading) return (
        <><Navbar /><div style={{ textAlign: "center", padding: "120px", fontSize: "18px", color: "#777" }}>Loading gallery...</div><Footer /></>
    );

    if (!folder || folder.message) return (
        <><Navbar />
        <div style={{ textAlign: "center", padding: "120px" }}>
            <h3 style={{ color: "#ef4444" }}>Gallery not found</h3>
            <button className="gallery-back-btn" onClick={() => navigate("/gallery")} style={{ marginTop: "20px" }}>
                <i className="fa-solid fa-arrow-left"></i> Back to Gallery
            </button>
        </div><Footer /></>
    );

    return (
        <>
            <Navbar />
            <div className="gallery-detail-page">
                {/* Header */}
                <div className="gallery-detail-header">
                    <button className="gallery-back-btn" onClick={() => navigate("/gallery")}>
                        <i className="fa-solid fa-arrow-left"></i> Gallery
                    </button>
                    <div className="gallery-detail-title">
                        <h1>{folder.name}</h1>
                        <div className="gallery-detail-meta">
                            {folder.displayDate && <span><i className="fa-regular fa-calendar"></i> {folder.displayDate}</span>}
                            <span><i className="fa-solid fa-images"></i> {images.length} photo{images.length !== 1 ? "s" : ""}</span>
                        </div>
                        {folder.description && <p className="gallery-detail-desc">{folder.description}</p>}
                    </div>
                    {images.length > 0 && (
                        <button className="slideshow-start-btn" onClick={() => openSlideshow(0)}>
                            <i className="fa-solid fa-play"></i> Slideshow
                        </button>
                    )}
                </div>

                {/* Image Grid */}
                {images.length === 0 ? (
                    <div className="gallery-empty" style={{ marginTop: "60px" }}>
                        <i className="fa-solid fa-image"></i>
                        <h3>No images yet in this gallery.</h3>
                    </div>
                ) : (
                    <div className="gallery-image-grid">
                        {images.map((img, idx) => (
                            <div key={img._id} className="gallery-image-cell" onClick={() => setLightboxIdx(idx)}>
                                <img src={img.url} alt={img.caption || `Photo ${idx + 1}`} loading="lazy" />
                                <div className="gallery-image-overlay">
                                    <i className="fa-solid fa-expand"></i>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Footer />

            {/* Lightbox */}
            {lightboxIdx !== null && (
                <div className="pub-lightbox" onClick={() => setLightboxIdx(null)}>
                    <button className="lb-close" onClick={() => setLightboxIdx(null)}><i className="fa-solid fa-xmark"></i></button>
                    {lightboxIdx > 0 && (
                        <button className="lb-prev" onClick={e => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}>
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                    )}
                    <div className="lb-img-wrap" onClick={e => e.stopPropagation()}>
                        <img src={images[lightboxIdx]?.url} alt="" />
                        {images[lightboxIdx]?.caption && <p className="lb-caption">{images[lightboxIdx].caption}</p>}
                    </div>
                    {lightboxIdx < images.length - 1 && (
                        <button className="lb-next" onClick={e => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}>
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    )}
                    <div className="lb-counter">{lightboxIdx + 1} / {images.length}</div>
                </div>
            )}

            {/* Slideshow */}
            {slideshow && images.length > 0 && (
                <div className="slideshow-overlay">
                    {/* Controls bar */}
                    <div className="ss-topbar">
                        <div className="ss-title">{folder.name}</div>
                        <div className="ss-controls">
                            <button onClick={() => setSlideshowIdx(i => (i - 1 + images.length) % images.length)}>
                                <i className="fa-solid fa-backward-step"></i>
                            </button>
                            <button onClick={() => setSsPlaying(p => !p)}>
                                <i className={`fa-solid ${ssPlaying ? "fa-pause" : "fa-play"}`}></i>
                            </button>
                            <button onClick={() => setSlideshowIdx(i => (i + 1) % images.length)}>
                                <i className="fa-solid fa-forward-step"></i>
                            </button>
                            <span className="ss-counter">{slideshowIdx + 1} / {images.length}</span>
                            <button className="ss-close" onClick={() => setSlideshow(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    </div>

                    {/* Main image */}
                    <div className="ss-main">
                        <img
                            key={slideshowIdx}
                            src={images[slideshowIdx]?.url}
                            alt={images[slideshowIdx]?.caption || ""}
                            className="ss-image"
                        />
                        {images[slideshowIdx]?.caption && (
                            <div className="ss-caption">{images[slideshowIdx].caption}</div>
                        )}
                    </div>

                    {/* Thumbnail strip */}
                    <div className="ss-strip">
                        {images.map((img, idx) => (
                            <div
                                key={img._id}
                                className={`ss-thumb ${idx === slideshowIdx ? "active" : ""}`}
                                onClick={() => { setSlideshowIdx(idx); setSsPlaying(false); }}
                            >
                                <img src={img.url} alt="" />
                            </div>
                        ))}
                    </div>

                    {/* Progress bar */}
                    {ssPlaying && (
                        <div className="ss-progress-bar">
                            <div className="ss-progress-fill" key={slideshowIdx}></div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default GalleryDetail;
